import React, { useState, useEffect, useCallback, useRef } from 'react';
import { VariableSizeList as List } from 'react-window';
import { searchHybrid } from '../../../qwanyx-core/api-client';
import { DNDEditor } from '../../editor/DNDEditor';
import { QwantaProvider } from 'qwanyx';
import { getTemplateCache } from '../services/templateCache';
import { 
  getSelectionStyle, 
  getBackgroundColor 
} from '../utils/stackUtils';

/**
 * HybridView - Category + flat items view
 * 
 * This component provides a hybrid view that shows:
 * - Categories (folders) in hierarchical structure
 * - Items within each category displayed flat
 * - Search functionality across both categories and items
 * - Domain filtering support
 * - Template rendering for both categories and items
 * 
 * Props:
 * - rootId: Root ID to scope search within (optional)
 * - di: Domain ID for filtering (optional)
 * - query: Search query string
 * - fields: Fields to search in (default: all text fields)
 * - fuzzy: Enable fuzzy search
 * - facets: Facet fields to generate
 * - onSelect: Selection callback
 * - selectedItems: Currently selected items
 * - categoryTemplate: Template for rendering categories
 * - itemTemplate: Template for rendering items
 * - useCompact: Use compact rendering
 * - showItemCount: Show item count in category headers
 * - collapsible: Allow categories to be collapsed
 * - debug: Enable debug logging
 */

const HybridView = ({
  rootId = null,
  di = null,
  query = '',
  fields = [],
  fuzzy = false,
  facets = [],
  facetConfig = [], // Array of {field, label} objects for custom facet configuration
  onSelect,
  selectedItems = [],
  categoryTemplate = null,
  itemTemplate = null,
  useCompact = true,
  showItemCount = true,
  collapsible = true,
  debug = false,
  // UI props
  showSearchInfo = true,
  showLoadingState = true,
  showFacets = true,
  showFacetCounts = true,
  // Virtualization props
  virtualized = true,
  categoryHeight = 60,
  itemHeight = 50,
  overscanCount = 3,
  // Sorting props
  sortBy = 'title',
  sortOrder = 1
}) => {
  // State management
  const [hybridData, setHybridData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [facetData, setFacetData] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [categoryCount, setCategoryCount] = useState(0);
  const [itemCount, setItemCount] = useState(0);
  
  // Template state
  const [categoryTemplateData, setCategoryTemplateData] = useState(null);
  const [itemTemplateData, setItemTemplateData] = useState(null);
  const [processedCategoryTemplates, setProcessedCategoryTemplates] = useState(new Map());
  const [processedItemTemplates, setProcessedItemTemplates] = useState(new Map());
  
  // Collapsed state for categories
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  
  // Flattened data for virtualization
  const [flattenedData, setFlattenedData] = useState([]);
  const [itemSizes, setItemSizes] = useState({});
  
  // Refs for height measurement
  const containerRef = useRef(null);
  
  // Height state for virtualization
  const [containerHeight, setContainerHeight] = useState(400);
  
  // Measure container height for react-window
  useEffect(() => {
    const measureHeight = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newHeight = rect.height;
        if (newHeight !== containerHeight && newHeight > 0) {
          setContainerHeight(newHeight);
          if (debug) {
            console.log('[HybridView] Container height updated:', newHeight);
          }
        }
      }
    };
    
    // Use requestAnimationFrame to ensure DOM has updated
    const measureWithDelay = () => {
      requestAnimationFrame(() => {
        setTimeout(measureHeight, 100);
      });
    };
    
    measureWithDelay();
    
    // Set up ResizeObserver for more accurate height tracking
    let resizeObserver;
    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newHeight = entry.contentRect.height;
          if (newHeight !== containerHeight && newHeight > 0) {
            setContainerHeight(newHeight);
            if (debug) {
              console.log('[HybridView] ResizeObserver height update:', newHeight);
            }
          }
        }
      });
      resizeObserver.observe(containerRef.current);
    }
    
    // Fallback to window resize listener
    window.addEventListener('resize', measureWithDelay);
    
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', measureWithDelay);
    };
  }, [showSearchInfo, showFacets, showLoadingState, debug, containerHeight]);

  // Load templates
  useEffect(() => {
    if (categoryTemplate) {
      // Force refresh on mount to ensure fresh templates during development
      getTemplateCache().fetchTemplate(categoryTemplate, true)
        .then(template => {
          setCategoryTemplateData(template);
          if (debug) {
            console.log('[HybridView] Category template loaded with refresh:', categoryTemplate);
          }
        })
        .catch(error => {
          console.error('[HybridView] Error loading category template:', error);
        });
    } else {
      setCategoryTemplateData(null);
      setProcessedCategoryTemplates(new Map());
    }
  }, [categoryTemplate, debug]);

  useEffect(() => {
    if (itemTemplate) {
      // Force refresh on mount to ensure fresh templates during development
      getTemplateCache().fetchTemplate(itemTemplate, true)
        .then(template => {
          setItemTemplateData(template);
          if (debug) {
            console.log('[HybridView] Item template loaded with refresh:', itemTemplate);
          }
        })
        .catch(error => {
          console.error('[HybridView] Error loading item template:', error);
        });
    } else {
      setItemTemplateData(null);
      setProcessedItemTemplates(new Map());
    }
  }, [itemTemplate, debug]);

  // Process templates when data changes
  useEffect(() => {
    if (categoryTemplateData && hybridData.length > 0) {
      const newProcessedTemplates = new Map();
      
      hybridData.forEach(categoryGroup => {
        if (categoryGroup.category && categoryGroup.category._id !== 'uncategorized') {
          try {
            const processedTemplate = getTemplateCache().replaceTemplateVariables(
              categoryTemplateData, 
              categoryGroup.category
            );
            newProcessedTemplates.set(categoryGroup.category._id, processedTemplate);
          } catch (error) {
            console.error(`[HybridView] Error processing category template for ${categoryGroup.category._id}:`, error);
          }
        }
      });
      
      setProcessedCategoryTemplates(newProcessedTemplates);
    }
  }, [categoryTemplateData, hybridData, debug]);

  useEffect(() => {
    if (itemTemplateData && hybridData.length > 0) {
      const newProcessedTemplates = new Map();
      
      hybridData.forEach(categoryGroup => {
        categoryGroup.items.forEach(item => {
          try {
            const processedTemplate = getTemplateCache().replaceTemplateVariables(itemTemplateData, item);
            newProcessedTemplates.set(item._id, processedTemplate);
          } catch (error) {
            console.error(`[HybridView] Error processing item template for ${item._id}:`, error);
          }
        });
      });
      
      setProcessedItemTemplates(newProcessedTemplates);
    }
  }, [itemTemplateData, hybridData, debug]);

  // Search function
  const performSearch = useCallback(async () => {
    if (debug) {
      console.log('[HybridView] Performing search:', {
        rootId, di, query, fields, fuzzy, facets
      });
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await searchHybrid(
        rootId, di, query, fields, fuzzy, facets, sortBy, sortOrder
      );
      
      if (debug) {
        console.log('[HybridView] Search result:', result);
      }
      
      setHybridData(result.results);
      setFacetData(result.facets || {});
      setTotalCount(result.total);
      setCategoryCount(result.categories);
      setItemCount(result.items);
      
    } catch (err) {
      console.error('[HybridView] Search error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [rootId, di, query, fields, fuzzy, facets, debug, sortBy, sortOrder]);

  // Perform search when parameters change
  useEffect(() => {
    performSearch();
  }, [performSearch]);

  // Flatten data for virtualization when hybridData or collapsed state changes
  useEffect(() => {
    if (!virtualized) return;
    
    const flattened = [];
    const sizes = {};
    
    hybridData.forEach((categoryGroup, categoryIndex) => {
      const categoryId = `category-${categoryIndex}`;
      const isCollapsed = collapsedCategories.has(categoryGroup.category._id);
      
      // Add category header
      flattened.push({
        type: 'category',
        data: categoryGroup.category,
        itemCount: categoryGroup.items.length,
        isCollapsed,
        id: categoryId
      });
      sizes[categoryId] = categoryHeight + 8; // Add margin
      
      // Add items if not collapsed
      if (!isCollapsed) {
        categoryGroup.items.forEach((item, itemIndex) => {
          const itemId = `item-${categoryIndex}-${itemIndex}`;
          flattened.push({
            type: 'item',
            data: item,
            categoryData: categoryGroup.category,
            id: itemId
          });
          sizes[itemId] = itemHeight + 4; // Add margin
        });
      }
    });
    
    setFlattenedData(flattened);
    setItemSizes(sizes);
  }, [hybridData, collapsedCategories, virtualized, categoryHeight, itemHeight]);

  // Handle item selection
  const handleItemSelect = (item, isShiftKey = false, isCtrlKey = false) => {
    if (onSelect) {
      onSelect(item._id, isShiftKey, isCtrlKey);
    }
  };

  // Toggle category collapsed state
  const toggleCategoryCollapse = (categoryId) => {
    if (!collapsible) return;
    
    setCollapsedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Virtualized row renderer
  const VirtualizedRow = ({ index, style, data }) => {
    const rowData = data.flattenedData[index];
    
    if (rowData.type === 'category') {
      return (
        <div style={style}>
          {data.renderCategoryHeaderVirtualized(rowData)}
        </div>
      );
    } else {
      return (
        <div style={style}>
          {data.renderItemVirtualized(rowData)}
        </div>
      );
    }
  };

  // Get item size for variable list
  const getItemSize = (index) => {
    const rowData = flattenedData[index];
    return itemSizes[rowData?.id] || itemHeight;
  };

  // Get processed template for category or item
  const getProcessedCategoryTemplate = (categoryId) => {
    return processedCategoryTemplates.get(categoryId) || null;
  };

  const getProcessedItemTemplate = (itemId) => {
    return processedItemTemplates.get(itemId) || null;
  };

  // Render category header (virtualized version)
  const renderCategoryHeaderVirtualized = (rowData) => {
    const category = rowData.data;
    const itemCount = rowData.itemCount;
    const isCollapsed = rowData.isCollapsed;
    const isUncategorized = category._id === 'uncategorized';
    const processedTemplate = getProcessedCategoryTemplate(category._id);
    
    // Template rendering for category
    if (processedTemplate && !isUncategorized) {
      return (
        <div
          onClick={() => toggleCategoryCollapse(category._id)}
          style={{
            cursor: collapsible ? 'pointer' : 'default',
            marginBottom: '8px',
            height: 'calc(100% - 8px)',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <QwantaProvider _id={category._id}>
            <DNDEditor
              cardData={processedTemplate}
              enabled={false}
              readOnly={true}
              stackMode={false}
              compact={useCompact}
            />
          </QwantaProvider>
        </div>
      );
    }
    
    // Default category header
    const displayText = isUncategorized ? category.title : 
      (category.title || category.name || `Category ${category._id}`);
    
    return (
      <div
        onClick={() => toggleCategoryCollapse(category._id)}
        style={{
          padding: '12px 16px',
          backgroundColor: isUncategorized ? '#f5f5f5' : '#e3f2fd',
          borderRadius: '6px',
          cursor: collapsible ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #ddd',
          height: 'calc(100% - 8px)',
          marginBottom: '8px',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {collapsible && (
            <span style={{ fontSize: '12px', color: '#666' }}>
              {isCollapsed ? '▶' : '▼'}
            </span>
          )}
          <span style={{ fontSize: '16px' }}>
            {isUncategorized ? '📂' : '📁'}
          </span>
          <span style={{ fontSize: '14px', fontWeight: '600' }}>
            {displayText}
          </span>
        </div>
        {showItemCount && (
          <span style={{ 
            fontSize: '12px', 
            color: '#666',
            backgroundColor: 'rgba(255,255,255,0.7)',
            padding: '2px 6px',
            borderRadius: '10px'
          }}>
            {itemCount} items
          </span>
        )}
      </div>
    );
  };

  // Render individual item (virtualized version)
  const renderItemVirtualized = (rowData) => {
    const item = rowData.data;
    const isSelected = selectedItems.includes(item._id);
    const processedTemplate = getProcessedItemTemplate(item._id);
    
    // Template rendering
    if (processedTemplate) {
      return (
        <div
          onClick={(e) => handleItemSelect(item, e.shiftKey, e.ctrlKey)}
          style={{
            ...getSelectionStyle(isSelected, false, true),
            backgroundColor: getBackgroundColor(isSelected, false),
            padding: '8px',
            marginLeft: '24px',
            marginBottom: '4px',
            borderRadius: '4px',
            cursor: 'pointer',
            border: '1px solid #eee',
            height: 'calc(100% - 4px)',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <QwantaProvider _id={item._id}>
            <DNDEditor
              cardData={processedTemplate}
              enabled={false}
              readOnly={true}
              stackMode={false}
              compact={useCompact}
            />
          </QwantaProvider>
        </div>
      );
    }
    
    // Default item rendering
    const displayText = item.title || 
      (item.firstname && item.name ? `${item.firstname} ${item.name}` : 'Untitled');
    
    return (
      <div
        onClick={(e) => handleItemSelect(item, e.shiftKey, e.ctrlKey)}
        style={{
          ...getSelectionStyle(isSelected, false, true),
          backgroundColor: getBackgroundColor(isSelected, false),
          padding: '8px 12px',
          marginLeft: '24px',
          marginBottom: '4px',
          borderRadius: '4px',
          cursor: 'pointer',
          border: '1px solid #eee',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          height: 'calc(100% - 4px)',
          boxSizing: 'border-box'
        }}
      >
        <span style={{ fontSize: '14px' }}>📄</span>
        <span style={{ fontSize: '13px', flex: 1 }}>{displayText}</span>
        {item.brief && (
          <span style={{ fontSize: '11px', color: '#666' }}>{item.brief}</span>
        )}
      </div>
    );
  };

  // Render category header (non-virtualized version)
  const renderCategoryHeader = (categoryGroup) => {
    const category = categoryGroup.category;
    const isUncategorized = category._id === 'uncategorized';
    const isCollapsed = collapsedCategories.has(category._id);
    const processedTemplate = getProcessedCategoryTemplate(category._id);
    
    // Template rendering for category
    if (processedTemplate && !isUncategorized) {
      return (
        <div
          onClick={() => toggleCategoryCollapse(category._id)}
          style={{
            cursor: collapsible ? 'pointer' : 'default',
            marginBottom: '8px'
          }}
        >
          <QwantaProvider _id={category._id}>
            <DNDEditor
              cardData={processedTemplate}
              enabled={false}
              readOnly={true}
              stackMode={false}
              compact={useCompact}
            />
          </QwantaProvider>
        </div>
      );
    }
    
    // Default category header
    const displayText = isUncategorized ? category.title : 
      (category.title || category.name || `Category ${category._id}`);
    
    return (
      <div
        onClick={() => toggleCategoryCollapse(category._id)}
        style={{
          padding: '12px 16px',
          backgroundColor: isUncategorized ? '#f5f5f5' : '#e3f2fd',
          borderRadius: '6px',
          marginBottom: '8px',
          cursor: collapsible ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #ddd'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {collapsible && (
            <span style={{ fontSize: '12px', color: '#666' }}>
              {isCollapsed ? '▶' : '▼'}
            </span>
          )}
          <span style={{ fontSize: '16px' }}>
            {isUncategorized ? '📂' : '📁'}
          </span>
          <span style={{ fontSize: '14px', fontWeight: '600' }}>
            {displayText}
          </span>
        </div>
        {showItemCount && (
          <span style={{ 
            fontSize: '12px', 
            color: '#666',
            backgroundColor: 'rgba(255,255,255,0.7)',
            padding: '2px 6px',
            borderRadius: '10px'
          }}>
            {categoryGroup.items.length} items
          </span>
        )}
      </div>
    );
  };

  // Render individual item
  const renderItem = (item) => {
    const isSelected = selectedItems.includes(item._id);
    const processedTemplate = getProcessedItemTemplate(item._id);
    
    // Template rendering
    if (processedTemplate) {
      return (
        <div
          key={item._id}
          onClick={(e) => handleItemSelect(item, e.shiftKey, e.ctrlKey)}
          style={{
            ...getSelectionStyle(isSelected, false, true),
            backgroundColor: getBackgroundColor(isSelected, false),
            padding: '8px',
            marginBottom: '4px',
            marginLeft: '24px',
            borderRadius: '4px',
            cursor: 'pointer',
            border: '1px solid #eee'
          }}
        >
          <QwantaProvider _id={item._id}>
            <DNDEditor
              cardData={processedTemplate}
              enabled={false}
              readOnly={true}
              stackMode={false}
              compact={useCompact}
            />
          </QwantaProvider>
        </div>
      );
    }
    
    // Default item rendering
    const displayText = item.title || 
      (item.firstname && item.name ? `${item.firstname} ${item.name}` : 'Untitled');
    
    return (
      <div
        key={item._id}
        onClick={(e) => handleItemSelect(item, e.shiftKey, e.ctrlKey)}
        style={{
          ...getSelectionStyle(isSelected, false, true),
          backgroundColor: getBackgroundColor(isSelected, false),
          padding: '8px 12px',
          marginBottom: '4px',
          marginLeft: '24px',
          borderRadius: '4px',
          cursor: 'pointer',
          border: '1px solid #eee',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <span style={{ fontSize: '14px' }}>📄</span>
        <span style={{ fontSize: '13px', flex: 1 }}>{displayText}</span>
        {item.brief && (
          <span style={{ fontSize: '11px', color: '#666' }}>{item.brief}</span>
        )}
      </div>
    );
  };

  // Get display label for a field (from facetConfig or fallback to field name)
  const getFacetLabel = (fieldName) => {
    const config = facetConfig.find(f => f.field === fieldName);
    return config ? config.label : fieldName;
  };

  // Render facets - only show configured facets
  const renderFacets = () => {
    if (Object.keys(facetData).length === 0 || facetConfig.length === 0) return null;
    
    // Filter facetData to only include configured facets
    const configuredFacets = facetConfig
      .map(config => ({
        field: config.field,
        label: config.label,
        values: facetData[config.field] || []
      }))
      .filter(facet => facet.values.length > 0);
    
    if (configuredFacets.length === 0) return null;
    
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #eee',
        fontSize: '12px'
      }}>
        <div style={{ fontWeight: '600', marginBottom: '8px' }}>Filters:</div>
        {configuredFacets.map(({ field, label, values }) => (
          <div key={field} style={{ marginBottom: '8px' }}>
            <div style={{ fontWeight: '500', marginBottom: '4px' }}>{label}:</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {values
                .filter(({ value }) => value !== null && value !== undefined && value !== "")
                .slice(0, 5)
                .map(({ value, count }) => (
                <span
                  key={value}
                  style={{
                    padding: '2px 6px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '10px',
                    fontSize: '10px'
                  }}
                >
                  {showFacetCounts ? `${value} (${count})` : value}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search info header */}
      {showSearchInfo && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #eee',
          fontSize: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <span style={{ fontWeight: '600' }}>Hybrid View</span>
            {query && <span style={{ marginLeft: '8px', color: '#666' }}>"{query}"</span>}
            {sortBy && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#999' }}>sorted by {sortBy} {sortOrder === 1 ? '↑' : '↓'}</span>}
          </div>
          <div style={{ fontSize: '12px', color: '#666', display: 'flex', gap: '12px' }}>
            <span>{categoryCount} categories</span>
            <span>{itemCount} items</span>
            <span>{totalCount} total</span>
          </div>
        </div>
      )}

      {/* Facets */}
      {showFacets && renderFacets()}

      {/* Loading state */}
      {showLoadingState && loading && (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: '#666'
        }}>
          <div style={{ fontSize: '16px', marginBottom: '8px' }}>🔄 Loading...</div>
          <div style={{ fontSize: '12px' }}>Searching categories and items</div>
        </div>
      )}


      {/* Error display */}
      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderBottom: '1px solid #eee'
        }}>
          ❌ Search error: {error}
        </div>
      )}

      {/* Hybrid content */}
      <div 
        ref={containerRef}
        style={{ 
          flex: 1, 
          overflow: 'hidden',
          padding: virtualized ? '0' : '16px'
        }}
      >
        {!loading && hybridData.length === 0 && !error && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#666'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <div style={{ fontSize: '16px', marginBottom: '8px' }}>No results found</div>
            <div style={{ fontSize: '14px' }}>
              {query ? `Try adjusting your search for "${query}"` : 'No categories or items to display'}
            </div>
          </div>
        )}

        {virtualized ? (
          // Virtualized rendering - handles unlimited items
          flattenedData.length > 0 && (
            <List
              height={containerHeight}
              itemCount={flattenedData.length}
              itemSize={getItemSize}
              itemData={{
                flattenedData,
                selectedItems,
                handleItemSelect,
                toggleCategoryCollapse,
                renderCategoryHeaderVirtualized,
                renderItemVirtualized,
                useCompact,
                collapsible,
                showItemCount
              }}
              overscanCount={overscanCount}
            >
              {VirtualizedRow}
            </List>
          )
        ) : (
          // Non-virtualized fallback
          hybridData.map((categoryGroup, index) => {
            const isCollapsed = collapsedCategories.has(categoryGroup.category._id);
            
            return (
              <div key={categoryGroup.category._id || index} style={{ marginBottom: '16px' }}>
                {renderCategoryHeader(categoryGroup)}
                
                {!isCollapsed && (
                  <div>
                    {categoryGroup.items.length === 0 ? (
                      <div style={{
                        marginLeft: '24px',
                        padding: '12px',
                        color: '#666',
                        fontSize: '12px',
                        fontStyle: 'italic'
                      }}>
                        No items in this category
                      </div>
                    ) : (
                      categoryGroup.items.map(item => renderItem(item))
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}

      </div>
    </div>
  );
};

export default HybridView;